import { cn } from "@/lib/utils";
import { CalendarDays, Clock, MapPin, ChevronRight } from "lucide-react";
import EventoImagen from "@/components/common/EventoImagen";
import { formatearFechaLarga } from "@/utils/fechas";

const estadoStyles = {
  "Venta disponible":
    "bg-accent/15 text-accent border-accent/40",
  "Últimos lugares":
    "bg-primary/10 text-primary dark:text-accent border-primary/30 dark:border-accent/40",
  Próximamente:
    "bg-muted text-muted-foreground border-border",
  Agotado:
    "bg-destructive/10 text-destructive border-destructive/30",
};

/**
 * Tarjeta privada de evento (socio autenticado).
 * Incluye imagen horizontal, salón, precio y CTA "Ver evento".
 */
export default function EventoPrivadoCard({ evento, testId, onVer }) {
  const badge = estadoStyles[evento.estado] || estadoStyles["Próximamente"];

  return (
    <article
      data-testid={testId}
      className={cn(
        "group rounded-2xl border hairline bg-card overflow-hidden",
        "shadow-soft transition-shadow duration-300 motion-reduce:transition-none",
        "hover:shadow-elevated",
      )}
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
        <EventoImagen
          src={evento.imagen}
          eventoId={evento.id}
          alt={evento.nombre}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 [@media(hover:hover)]:group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        />
      </div>

      <div className="p-6 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2 min-h-[1.75rem]">
            <span
              className={cn(
                "inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap",
                badge,
              )}
            >
              {evento.estado}
            </span>
          </div>
          <h3 className="font-heading text-xl text-foreground leading-snug break-words">
            {evento.nombre}
          </h3>
        </div>

        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{formatearFechaLarga(evento.fecha)}</span>
          </li>
          <li className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{evento.hora}</span>
          </li>
          <li className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{evento.salon}</span>
          </li>
        </ul>

        <div className="pt-2 border-t hairline flex items-end justify-between gap-3">
          <div className="flex flex-col">
            <span className="overline text-muted-foreground">Precio</span>
            <span
              data-testid={`${testId}-precio`}
              className="font-heading text-lg text-foreground mt-1"
            >
              {evento.precioMultiple ? "Ver precios" : evento.precio}
            </span>
          </div>
          <button
            type="button"
            data-testid={`${testId}-ver`}
            onClick={onVer}
            className={cn(
              "inline-flex items-center gap-2 rounded-full",
              "px-5 py-2.5 text-sm font-medium",
              "bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground",
              "transition-all duration-200 active:scale-[0.98] hover:opacity-90",
              "ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft",
            )}
          >
            Ver evento
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </article>
  );
}
